import { Action, Module, VuexModule, Mutation, getModule } from 'vuex-module-decorators';
import axios from 'axios';
import printJS from 'print-js';
import store from '@/store';
import { Leaflet } from '@/types/leaflet';
import { Fully } from '@/types/fully';
declare let fully: Fully;

export interface LeafletsStatus {
  interval: number;
  wait: number;
  limit: number;
  current: Leaflet | null;
  url: string;
  isPlaying: boolean;
  getPrevPage: number;
  getNextPage: number;
  pdf: string | null;
}

@Module({ dynamic: true, store, name: 'leaflets', namespaced: true, preserveState: false })
class LeafletsModlue extends VuexModule implements LeafletsStatus {
  /**
   * インターバル間隔
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public interval = 10 * 1000;

  /**
   * 自動再生復帰までの待機時間
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public wait = 0;

  /**
   * 表示制限数
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  public limit = 0;

  /**
   * カタログ情報
   *
   * @type {Leaflet[]}
   * @memberof LeafletsModlue
   */
  private datas: Leaflet[] = [];

  /**
   * 現在表示中のカタログインデックス
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  private _index = 0;

  /**
   * 現在表示中のカタログのページ数
   *
   * @type {number}
   * @memberof LeafletsModlue
   */
  private _page = 0;

  /**
   * 再生中かどうか
   *
   * @type {boolean}
   * @memberof LeafletsModlue
   */
  private _playing = false;

  /**
   * タイマーID
   *
   * @type {NodeJS.Timeout}
   * @memberof LeafletsModlue
   */
  private _timerId?: NodeJS.Timeout;

  /**
   * 現在表示中の広告データを取得
   *
   * @readonly
   * @type {(Leaflet | null)}
   * @memberof LeafletsModlue
   */
  public get current(): Leaflet | null {
    if (this.datas.length > 0) {
      return this.datas[this._index];
    }
    return null;
  }

  /**
   * 現在表示中のチラシ画像URLを取得
   *
   * @readonly
   * @type {string}
   * @memberof LeafletsModlue
   */
  public get url(): string {
    let url = '';
    const leaflet = this.current;
    if (leaflet !== null) {
      url = leaflet.items[this._page];
    }
    return url;
  }

  /**
   * 再生中かどうかを取得
   *
   * @readonly
   * @type {boolean}
   * @memberof LeafletsModlue
   */
  public get isPlaying(): boolean {
    return this._playing;
  }

  /**
   * 前のページ番号を取得
   *
   * @readonly
   * @type {number}
   * @memberof LeafletsModlue
   */
  public get getPrevPage(): number {
    let page = 0;
    const leaflet = this.current;
    if (leaflet !== null) {
      page = this._page - 1;
      if (page < 0) {
        page = leaflet.items.length - 1;
      }
    }
    return page;
  }

  /**
   * 次のページ番号を取得
   *
   * @readonly
   * @type {number}
   * @memberof LeafletsModlue
   */
  public get getNextPage(): number {
    let page = 0;
    const leaflet = this.current;
    if (leaflet !== null) {
      page = this._page + 1;
      if (page > leaflet.items.length - 1) {
        page = 0;
      }
    }
    return page;
  }

  /**
   * PDFデータ取得
   *
   * @readonly
   * @type {(string | null)}
   * @memberof LeafletsModlue
   */
  public get pdf(): string | null {
    const leaflet = this.current;
    if (leaflet !== null) {
      return leaflet.pdf || null;
    }
    return null;
  }

  /**
   * インターバル間隔をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setInterval(value: number) {
    this.interval = value;
  }

  /**
   * 自動再生復帰までの待機時間をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setWait(value: number) {
    this.wait = value;
  }

  /**
   * 表示制限数
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  public setLimit(value: number) {
    this.limit = value;
  }

  /**
   * カタログ情報をセット
   *
   * @param {Leaflet[]} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setDatas(value: Leaflet[]) {
    this.datas = value;
  }

  /**
   * 現在表示中のカタログインデックスをセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setIndex(value: number) {
    this._index = value;
  }

  /**
   * 現在表示中のカタログのページ数をセット
   *
   * @param {number} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setPage(value: number) {
    this._page = value;
  }

  /**
   * 再生中かどうかをセット
   *
   * @param {boolean} value
   * @memberof LeafletsModlue
   */
  @Mutation
  setPlaying(value: boolean) {
    this._playing = value;
  }

  /**
   * カタログ読み込み開始
   *
   * @returns {Promise<boolean>}
   * @memberof LeafletsModlue
   */
  @Action({})
  async load(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const config = {
        params: {
          limit: this.limit
        }
      };
      axios
        .get(process.env.VUE_APP_API_DOMAIN + '/api/leaflets', config)
        .then(response => {
          const leaflets: Leaflet[] = response.data;
          Promise.all(
            leaflets.map(async leaflet => {
              leaflet.items = await Promise.all(
                leaflet.items.map(async url => {
                  return await this.loadImage(url);
                })
              );
              return leaflet;
            })
          ).then((leaflets: Leaflet[]) => {
            // 全ての画像読み込みが完了したら、データ適応し再生開始
            this.setDatas(leaflets);
            this.setIndex(0);
            this.setPage(0);
            this.play();
            resolve();
          });
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * 画像読み込み
   *
   * @param {string} url
   * @returns {Promise<string>}
   * @memberof LeafletsModlue
   */
  @Action({})
  async loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      axios
        .get(url, { responseType: 'arraybuffer' })
        .then(response => {
          const prefix = `data:${response.headers['content-type']};base64,`;
          const base64 = new Buffer(response.data, 'binary').toString('base64');
          resolve(prefix + base64);
        })
        .catch(error => {
          reject(error);
        });
    });
  }

  /**
   * 読み込んだカタログを削除
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  delete() {
    this._timerId && clearTimeout(this._timerId);
  }

  /**
   * 前のカタログに戻る
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  prev() {
    let index = this._index - 1;
    if (index < 0) {
      index = this.datas.length - 1;
    }
    this.setIndex(index);
    this.setPage(0);
    this.play();
  }

  /**
   * 次のカタログへ進む
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  next() {
    let index = this._index + 1;
    if (index > this.datas.length - 1) {
      index = 0;
    }
    this.setIndex(index);
    this.setPage(0);
    this.play();
  }

  /**
   * 前のページ／裏面反転
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  prevPage() {
    this.setPage(this.getPrevPage);
    this.play();
  }

  /**
   * 次のページ／裏面反転
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  nextPage() {
    this.setPage(this.getNextPage);
    this.play();
  }

  /**
   * 自動再生開始
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  play() {
    this._timerId && clearTimeout(this._timerId);
    this.setPlaying(true);

    this._timerId = setTimeout(() => {
      this._timerId && clearTimeout(this._timerId);

      // 全ページ自動表示フラグがONになっているチラシデータの場合、次のページへ進める
      if (this.current !== null && this.current.is_pageauto) {
        if (this.getNextPage > 0) {
          this.nextPage();
        } else {
          // 最後のページの場合は次のチラシへ進める
          this.next();
        }
      } else {
        this.next();
      }
    }, this.interval);
  }

  /**
   * 自動再生停止
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  stop() {
    this._timerId && clearTimeout(this._timerId);
    this.setPlaying(false);

    // 待機時間が0以上のときは、待機時間後に自動再生復帰
    if (this.wait > 0) {
      this._timerId = setTimeout(() => {
        this._timerId && clearTimeout(this._timerId);
        this.next();
      }, this.wait);
    }
  }

  /**
   * 印刷
   *
   * @memberof LeafletsModlue
   */
  @Action({})
  print() {
    try {
      // Kioskモードでは全体を印刷
      fully.print();
    } catch (e) {
      const pdf = this.pdf;
      if (pdf !== null) {
        printJS(pdf);
      }
    }
    if (process.env.NODE_ENV === 'development') {
      console.log('fully.print()');
    }
  }
}

export default getModule(LeafletsModlue, store);
